"use client";

import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/providers/auth-provider";
import { MatchInvitationService } from "@/lib/services/matches/invitation.service";
import type { MatchInvitation, OpponentProfile } from "@/components/matchmaking/types";
import { useToast } from "@/hooks/use-toast";

interface InvitationDialogState {
  isOpen: boolean;
  invitation: MatchInvitation | null;
  opponent: OpponentProfile | null;
  type: "received" | "accepted" | null;
}

export function useMatchInvitations() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [invitationService] = useState(() => MatchInvitationService.getInstance());
  
  const [dialogState, setDialogState] = useState<InvitationDialogState>({
    isOpen: false,
    invitation: null,
    opponent: null,
    type: null,
  });

  const [isProcessing, setIsProcessing] = useState(false);

  // Handle incoming invitation
  const handleInvitationReceived = useCallback((invitation: MatchInvitation, opponent: OpponentProfile) => {
    console.log("Received invitation:", invitation, opponent);
    
    setDialogState({
      isOpen: true,
      invitation,
      opponent,
      type: "received",
    });

    // Show notification toast
    toast({
      title: "🎯 Match Challenge Received!",
      description: `${opponent.displayName} challenged you to a golf match`,
      duration: 6000,
    });
  }, [toast]);

  // Handle invitation acceptance (first user accepted, now asking challenger to confirm)
  const handleInvitationAccepted = useCallback((invitation: MatchInvitation) => {
    console.log("Someone accepted your invitation:", invitation);
    
    // For accepted invitations, we need to get the opponent profile
    const mockOpponent: OpponentProfile = {
      id: invitation.challengedId,
      name: "Golf Player",
      displayName: "Golf Player",
      mmr: 1500,
      skillTier: "intermediate",
      matchesPlayed: 25,
      winRate: 60,
      lastActive: "2 minutes ago",
      distance: 3.2,
      compatibilityScore: 85,
    };

    setDialogState({
      isOpen: true,
      invitation,
      opponent: mockOpponent,
      type: "accepted",
    });

    // Show notification toast
    toast({
      title: "✅ Challenge Accepted!",
      description: "Someone accepted your match challenge. Confirm to finalize!",
      duration: 8000,
    });
  }, [toast]);

  // Handle invitation decline
  const handleInvitationDeclined = useCallback((invitation: MatchInvitation) => {
    console.log("Invitation declined:", invitation);
    
    toast({
      title: "😔 Challenge Declined",
      description: "Your match challenge was declined",
      variant: "default",
    });
  }, [toast]);

  // Handle match confirmation
  const handleMatchConfirmed = useCallback((invitation: MatchInvitation) => {
    console.log("Match confirmed:", invitation);
    
    toast({
      title: "🎉 Match Confirmed!",
      description: "Your golf match is confirmed. Time to coordinate tee times!",
      duration: 10000,
    });
  }, [toast]);

  // Set up real-time listeners
  useEffect(() => {
    if (!user?.id) return;

    console.log("Setting up invitation listeners for user:", user.id);

    const unsubscribe = invitationService.subscribeToInvitations(user.id, {
      onInvitationReceived: handleInvitationReceived,
      onInvitationAccepted: handleInvitationAccepted,
      onInvitationDeclined: handleInvitationDeclined,
      onMatchConfirmed: handleMatchConfirmed,
    });

    return unsubscribe;
  }, [user?.id, handleInvitationReceived, handleInvitationAccepted, handleInvitationDeclined, handleMatchConfirmed]);

  // Accept invitation (first acceptance)
  const acceptInvitation = useCallback(async (invitationId: string) => {
    if (!user?.id || isProcessing) return;

    setIsProcessing(true);
    
    try {
      await invitationService.acceptMatchInvitation(invitationId, user.id);
      
      setDialogState(prev => ({ ...prev, isOpen: false }));
      
      toast({
        title: "✅ Challenge Accepted!",
        description: "Waiting for the challenger to confirm the match",
      });
    } catch (error) {
      console.error("Error accepting invitation:", error);
      toast({
        title: "Error",
        description: "Failed to accept challenge. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  }, [user?.id, isProcessing, invitationService, toast]);

  // Decline invitation
  const declineInvitation = useCallback(async (invitationId: string, reason?: string) => {
    if (!user?.id || isProcessing) return;

    setIsProcessing(true);
    
    try {
      await invitationService.declineMatchInvitation(invitationId, user.id, reason);
      
      setDialogState(prev => ({ ...prev, isOpen: false }));
      
      toast({
        title: "Challenge Declined",
        description: "You declined the match challenge",
      });
    } catch (error) {
      console.error("Error declining invitation:", error);
      toast({
        title: "Error",
        description: "Failed to decline challenge. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  }, [user?.id, isProcessing, invitationService, toast]);

  // Confirm match (second acceptance by challenger)
  const confirmMatch = useCallback(async (invitationId: string) => {
    if (!user?.id || isProcessing) return;

    setIsProcessing(true);
    
    try {
      await invitationService.confirmMatch(invitationId, user.id);
      
      setDialogState(prev => ({ ...prev, isOpen: false }));
      
      toast({
        title: "🎉 Match Confirmed!",
        description: "Your match is confirmed! Time to coordinate your tee time.",
        duration: 10000,
      });
    } catch (error) {
      console.error("Error confirming match:", error);
      toast({
        title: "Error",
        description: "Failed to confirm match. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  }, [user?.id, isProcessing, invitationService, toast]);

  // Close dialog
  const closeDialog = useCallback(() => {
    if (isProcessing) return; // Don't allow closing while processing
    
    setDialogState({
      isOpen: false,
      invitation: null,
      opponent: null,
      type: null,
    });
  }, [isProcessing]);

  return {
    // Dialog state
    dialogState,
    isProcessing,
    
    // Actions
    acceptInvitation,
    declineInvitation,
    confirmMatch,
    closeDialog,
  };
}
